/** @prettier */

import {
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
} from '@mui/material';
import React, {useState} from 'react';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';
//API import
import {
  patchCompositionsRTUnit,
  getCompositionsRTUnitByUnitID,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {sortByUnicode} from '../../functions/sort';

/**
 * 室内機削除ダイアログ
 * @param props props
 * @returns JSX.Element
 */
const IUDeleteDialog = (props: {
  /** 削除する室内機ID */
  iuID: string;
  /** 削除する室内機の名前 */
  iuName: string;
  /** 削除する室内機が登録されている熱源機の情報 */
  ouInfo: apiIF.res.getCompositionsRTUnitByUnitID | null;
  /** 削除する室内機が登録されている熱源機の情報（set関数） */
  setOUInfo: React.Dispatch<
    React.SetStateAction<apiIF.res.getCompositionsRTUnitByUnitID | null>
  >;
  /** ダイアログを開くかどうか */
  isOpenDialog: boolean;
  /** ダイアログを開くかどうか（set関数） */
  setIsOpenDialog: React.Dispatch<React.SetStateAction<boolean>>;
  /** 課金しているかどうか */
  isBilling: boolean;
  /** GW毎の構成情報未反映の有無（set関数） */
  setHasConfigGW: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数
  //パスパラメータ取得
  const {siteID, gwID, ouID} = useParams();

  const [checked1, setChecked1] = useState(false); //1行目のチェックボックス
  const [checked2, setChecked2] = useState(false); //2行目のチェックボックス
  const [okBtnDisabled, setOKBtnDisabled] = useState(true); //OKボタン非表示フラグ
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false); //APIエラー時エラーメッセージ表示
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * OKボタン押下時処理
   */
  const deleteIU = () => {
    //パスパラメータを取得できない場合
    //室内外機の情報を取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      ouID === undefined ||
      props.ouInfo === null
    ) {
      return;
    }

    setOKBtnDisabled(true);
    setAPIWait(true);

    //APIに送信するデータを作成
    const deleteData: apiIF.reqParameters.patchCompositionsRTUnit = {
      'indoor-unit-informations': props.ouInfo[
        'indoor-unit-informations'
      ].filter(iuInfo => iuInfo['unit-id'] !== props.iuID),
    };
    //情報を編集
    patchCompositionsRTUnit(siteID, gwID, ouID, deleteData)
      .then(() => {
        //室内外機の情報を取得
        return getCompositionsRTUnitByUnitID(siteID, gwID, ouID);
      })
      .then(response => {
        //表示内容をUnicode順に並び替え
        response['indoor-unit-informations'].sort(
          sortByUnicode('indoor-unit-name')
        );

        props.setOUInfo(response);
        props.setHasConfigGW(props.isBilling); //課金している場合のみ表示
        handleClose();
      })
      .catch(() => {
        setIsOpenErrorDialog(true);
      })
      .finally(() => {
        setAPIWait(false);
      });
  };
  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
    setChecked1(false);
    setChecked2(false);
  };

  /**
   * チェックボックス押下時処理
   * @param event
   * @param check 押下したチェックボックスの名前
   */
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    check: string
  ) => {
    if (check === 'check1') {
      setChecked1(event.target.checked);
      if (event.target.checked === false || checked2 === false) {
        setOKBtnDisabled(true);
      } else {
        setOKBtnDisabled(false);
      }
    }
    if (check === 'check2') {
      setChecked2(event.target.checked);
      if (event.target.checked === false || checked1 === false) {
        setOKBtnDisabled(true);
      } else {
        setOKBtnDisabled(false);
      }
    }
  };

  return (
    <>
      <Dialog
        open={props.isOpenDialog}
        onClose={handleClose}
        aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">
          {trans('IUDeleteDialog.remoteTerminalUnit.title')}
        </DialogTitle>
        <DialogContent>
          {/* 室内機名称の表示 */}
          <DialogContentText>
            {trans('IUDeleteDialog.remoteTerminalUnit.msg')} {props.iuName}
          </DialogContentText>
          {/* チェックボックス */}
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={e => handleChange(e, 'check1')}
                  id={`mv-Check1DeleteIu${props.iuName}`}
                />
              }
              checked={checked1}
              label={trans('IUDeleteDialog.checkboxDescription.unrestorable')}
            />
          </FormGroup>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={e => handleChange(e, 'check2')}
                  id={`mv-Check2DeleteIu${props.iuName}`}
                />
              }
              checked={checked2}
              label={trans(
                'IUDeleteDialog.checkboxDescription.operationDataDeleted'
              )}
            />
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('IUDeleteDialog.buttonTxt.cancel')}
            id={`mv-btnCancelDeleteIu${props.iuName}`}
          />
          <PrimaryButton
            onClick={deleteIU}
            buttonText={trans('IUDeleteDialog.buttonTxt.ok')}
            id={`mv-btnOkDeleteIu${props.iuName}`}
            disabled={okBtnDisabled}
            apiWait={apiWait}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={`${props.iuName}DeleteErrDlgBtn`}
      />
    </>
  );
};
export default IUDeleteDialog;
